/**
 * TalentLens AI - API Client
 * Type-safe API client for backend communication
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || 'Request failed');
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {} as T;

    // Handle CSV downloads
    if (response.headers.get('content-type')?.includes('text/csv')) {
      return text as unknown as T;
    }

    return JSON.parse(text);
  }

  // Auth
  async login(email: string, password: string) {
    return this.request<{ success: boolean; data: { user: any; token: string } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  async me() {
    // Special handling for auth check - don't throw on 401
    const url = `${this.baseUrl}/auth/me`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status === 401) {
      // Not authenticated - return failure response instead of throwing
      return { success: false, data: null };
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || 'Request failed');
    }

    const text = await response.text();
    if (!text) return { success: false, data: null };

    return JSON.parse(text);
  }

  // CVs
  async getCVs(params?: Record<string, string>) {
    return this.request<{ success: boolean; data: { cvs: any[]; pagination: any } }>('/cvs', { params });
  }

  async getCV(id: string) {
    return this.request<{ success: boolean; data: any }>(`/cvs/${id}`);
  }

  async getUploadUrl(filename: string, contentType: string) {
    return this.request<{ success: boolean; data: { uploadUrl: string; objectKey: string } }>('/cvs/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    });
  }

  async registerCV(objectKey: string, filename: string) {
    return this.request<{ success: boolean; data: any }>('/cvs', {
      method: 'POST',
      body: JSON.stringify({ objectKey, filename }),
    });
  }

  async retryCV(id: string) {
    return this.request(`/cvs/${id}/retry`, { method: 'POST' });
  }

  async archiveCV(id: string, archived: boolean) {
    return this.request(`/cvs/${id}/archive`, {
      method: 'PATCH',
      body: JSON.stringify({ archived }),
    });
  }

  async deleteCV(id: string) {
    return this.request(`/cvs/${id}`, { method: 'DELETE' });
  }

  async getDownloadUrl(id: string) {
    return this.request<{ success: boolean; data: { downloadUrl: string } }>(`/cvs/${id}/download`);
  }

  // Jobs
  async getJobs(params?: Record<string, string>) {
    return this.request<{ success: boolean; data: { jobs: any[]; pagination: any } }>('/jobs', { params });
  }

  async getJob(id: string) {
    return this.request<{ success: boolean; data: any }>(`/jobs/${id}`);
  }

  async createJob(data: { title: string; description: string; priority?: string; team?: string }) {
    return this.request<{ success: boolean; data: any }>('/jobs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateJob(id: string, data: any) {
    return this.request(`/jobs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteJob(id: string) {
    return this.request(`/jobs/${id}`, { method: 'DELETE' });
  }

  async getJobVersions(id: string) {
    return this.request<{ success: boolean; data: any }>(`/jobs/${id}/versions`);
  }

  async rollbackJobVersion(id: string, version: number) {
    return this.request(`/jobs/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  }

  // Search
  async search(query: string, filters?: any, options?: { semantic?: boolean; limit?: number; cursor?: string }, signal?: AbortSignal) {
    return this.request<{ success: boolean; data: any }>('/search', {
      method: 'POST',
      body: JSON.stringify({ query, filters, ...options }),
      signal,
    });
  }

  async getSearchSuggestions(query: string, signal?: AbortSignal) {
    return this.request<{ success: boolean; data: { suggestions: any[] } }>('/search/suggestions', {
      params: { q: query },
      signal,
    });
  }

  async getSearchFilters() {
    return this.request<{ success: boolean; data: any }>('/search/filters');
  }

  // Matching
  async getJobMatches(jobId: string, params?: Record<string, string>) {
    return this.request<{ success: boolean; data: any }>(`/match/jobs/${jobId}/candidates`, { params });
  }

  async getCVMatches(cvId: string, params?: Record<string, string>) {
    return this.request<{ success: boolean; data: any }>(`/match/cvs/${cvId}/jobs`, { params });
  }

  async getMatchDetails(jobId: string, cvId: string) {
    return this.request<{ success: boolean; data: any }>(`/match/details/${jobId}/${cvId}`);
  }

  async batchMatch(jobId: string, cvIds: string[], includeExplanation?: boolean) {
    return this.request<{ success: boolean; data: any }>('/match/batch', {
      method: 'POST',
      body: JSON.stringify({ jobId, cvIds, includeExplanation }),
    });
  }

  async compareMatches(jobId: string, cvIds: string[]) {
    return this.request<{ success: boolean; data: any }>('/match/compare', {
      method: 'POST',
      body: JSON.stringify({ jobId, cvIds }),
    });
  }

  // Analytics
  async getDashboard() {
    return this.request<{ success: boolean; data: any }>('/analytics/dashboard');
  }

  async getSkillAnalytics(limit?: number) {
    return this.request<{ success: boolean; data: any }>('/analytics/skills', {
      params: limit ? { limit: limit.toString() } : undefined,
    });
  }

  async getPipelineAnalytics(days?: number) {
    return this.request<{ success: boolean; data: any }>('/analytics/pipeline', {
      params: days ? { days: days.toString() } : undefined,
    });
  }

  async getUserAnalytics(days?: number) {
    return this.request<{ success: boolean; data: any }>('/analytics/users', {
      params: days ? { days: days.toString() } : undefined,
    });
  }

  async getMatchingAnalytics(days?: number) {
    return this.request<{ success: boolean; data: any }>('/analytics/matching', {
      params: days ? { days: days.toString() } : undefined,
    });
  }

  async getAnalyticsTrends(days?: number) {
    return this.request<{ success: boolean; data: any }>('/analytics/trends', {
      params: days ? { days: days.toString() } : undefined,
    });
  }

  // Audit Logs
  async getAuditLogs(params?: {
    limit?: number;
    offset?: number;
    userId?: string;
    action?: string;
    resourceType?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams: Record<string, string> = {};
    if (params?.limit) queryParams.limit = params.limit.toString();
    if (params?.offset) queryParams.offset = params.offset.toString();
    if (params?.userId) queryParams.userId = params.userId;
    if (params?.action) queryParams.action = params.action;
    if (params?.resourceType) queryParams.resourceType = params.resourceType;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    return this.request<{ success: boolean; data: { logs: any[]; total: number } }>('/audit/logs', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
  }

  async getAuditLogStats() {
    return this.request<{ success: boolean; data: any }>('/audit/stats');
  }

  // Export
  async exportCVsCSV(cvIds?: string[], filters?: any) {
    return this.request<string>('/export/cvs/csv', {
      method: 'POST',
      body: JSON.stringify({ cvIds, filters }),
    });
  }

  async exportCVsJSON(cvIds?: string[], filters?: any) {
    return this.request<any[]>('/export/cvs/json', {
      method: 'POST',
      body: JSON.stringify({ cvIds, filters }),
    });
  }

  async exportMatchesCSV(matches: any[], jobTitle?: string) {
    return this.request<string>('/export/matches/csv', {
      method: 'POST',
      body: JSON.stringify({ matches, jobTitle }),
    });
  }

  // Pipeline
  async addToPipeline(data: { job_id: string; cv_id: string; stage?: string; notes?: string; rating?: number; tags?: string[] }) {
    return this.request<{ success: boolean; data: any }>('/pipeline', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPipelineForJob(jobId: string, params?: { stage?: string; includeRejected?: string }) {
    return this.request<{ success: boolean; data: any[] }>(`/pipeline/job/${jobId}`, { params });
  }

  async getPipelineEntry(id: string) {
    return this.request<{ success: boolean; data: any }>(`/pipeline/${id}`);
  }

  async updatePipelineStage(id: string, data: { stage: string; notes?: string }) {
    return this.request<{ success: boolean; data: any }>(`/pipeline/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updatePipeline(id: string, data: { notes?: string; rating?: number; tags?: string[] }) {
    return this.request<{ success: boolean; data: any }>(`/pipeline/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async rejectCandidate(id: string, reason: string) {
    return this.request<{ success: boolean; data: any }>(`/pipeline/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async removePipeline(id: string) {
    return this.request(`/pipeline/${id}`, { method: 'DELETE' });
  }

  async getPipelineStats(jobId: string) {
    return this.request<{ success: boolean; data: any }>(`/pipeline/stats/${jobId}`);
  }

  // Interviews
  async scheduleInterview(data: {
    pipeline_id: string;
    title: string;
    type: string;
    scheduled_at: string;
    duration_minutes?: number;
    location?: string;
    meeting_link?: string;
    interviewers: { user_id: string; name: string; email: string }[];
    candidate_email?: string;
    candidate_name?: string;
  }) {
    return this.request<{ success: boolean; data: any }>('/interviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInterviews(params?: { jobId?: string; status?: string; startDate?: string; endDate?: string; limit?: string; cursor?: string }) {
    return this.request<{ success: boolean; data: { interviews: any[]; pagination: any } }>('/interviews', { params });
  }

  async getUpcomingInterviews(days?: number) {
    return this.request<{ success: boolean; data: any[] }>('/interviews/upcoming', {
      params: days ? { days: days.toString() } : undefined,
    });
  }

  async getInterview(id: string) {
    return this.request<{ success: boolean; data: any }>(`/interviews/${id}`);
  }

  async updateInterview(id: string, data: any) {
    return this.request<{ success: boolean; data: any }>(`/interviews/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async cancelInterview(id: string) {
    return this.request(`/interviews/${id}`, { method: 'DELETE' });
  }

  async submitInterviewFeedback(id: string, data: {
    rating: number;
    recommendation: string;
    strengths: string;
    concerns: string;
    notes?: string;
  }) {
    return this.request<{ success: boolean; data: any }>(`/interviews/${id}/feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPipelineInterviews(pipelineId: string) {
    return this.request<{ success: boolean; data: any[] }>(`/interviews/pipeline/${pipelineId}`);
  }

  // Emails
  async sendEmail(data: {
    to: string;
    cc?: string[];
    subject: string;
    body: string;
    pipeline_id?: string;
    interview_id?: string;
    template_id?: string;
    placeholders?: Record<string, string>;
  }) {
    return this.request<{ success: boolean; data: any }>('/emails/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getEmailTemplates(type?: string) {
    return this.request<{ success: boolean; data: any[] }>('/emails/templates', {
      params: type ? { type } : undefined,
    });
  }

  async createEmailTemplate(data: {
    name: string;
    type: string;
    subject: string;
    body: string;
    is_default?: boolean;
  }) {
    return this.request<{ success: boolean; data: any }>('/emails/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmailTemplate(id: string, data: any) {
    return this.request<{ success: boolean; data: any }>(`/emails/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailTemplate(id: string) {
    return this.request(`/emails/templates/${id}`, { method: 'DELETE' });
  }

  async getEmailLogs(params?: { pipelineId?: string; status?: string; limit?: string; cursor?: string }) {
    return this.request<{ success: boolean; data: { logs: any[]; pagination: any } }>('/emails/logs', { params });
  }

  async aiComposeEmail(data: {
    purpose: 'interview_invite' | 'follow_up' | 'rejection' | 'offer' | 'general';
    candidateName: string;
    candidateEmail?: string;
    jobTitle?: string;
    companyName?: string;
    additionalContext?: string;
    tone?: 'formal' | 'friendly' | 'professional';
  }) {
    return this.request<{ success: boolean; data: { subject: string; body: string } }>('/emails/ai-compose', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async previewEmail(data: { subject: string; body: string; placeholders: Record<string, string> }) {
    return this.request<{ success: boolean; data: any }>('/emails/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getEmailStatus() {
    return this.request<{ success: boolean; data: { configured: boolean; connected: boolean } }>('/emails/status');
  }

  // Admin endpoints
  async getProcessingLogs(params?: { limit?: string; status?: string; cv_id?: string }) {
    return this.request<{ success: boolean; data: { logs: any[]; count: number } }>('/admin/logs/processing', { params });
  }

  async getErrorLogs(params?: { limit?: string }) {
    return this.request<{ success: boolean; data: { errors: any[]; count: number } }>('/admin/logs/errors', { params });
  }

  async getAppLogs(params?: { lines?: string; level?: string }) {
    return this.request<{ success: boolean; data: { logs: any[]; count: number; message?: string } }>('/admin/logs/app', { params });
  }

  async getSystemStatus() {
    return this.request<{ success: boolean; data: any }>('/admin/system/status');
  }
}

export const api = new ApiClient(API_BASE_URL);
export default api;
